import axios from "axios";
import {
  SUPER_ADMIN_USERS_URL,
  SUPER_ADMIN_USER_URL,
  SUPER_ADMIN_IMPERSONATE_URL,
  SUPER_ADMIN_SUSPEND_URL,
  SUPER_ADMIN_UNSUSPEND_URL,
  SUPER_ADMIN_HARD_DELETE_URL,
  SUPER_ADMIN_UPDATE_ROLES_URL,
  SUPER_ADMIN_TEST_DATA_URL,
  SUPER_ADMIN_LOGS_URL,
  STOP_IMPERSONATION_URL,
} from "../constants";

const withAuth = (token) => ({
  headers: {
    authorization: token,
  },
});

export const fetchSuperAdminUsers = async (token, query) => {
  try {
    const response = await axios.get(SUPER_ADMIN_USERS_URL, {
      ...withAuth(token),
      params: {
        page: query.page,
        pageSize: query.pageSize,
        search: query.search || undefined,
        sortBy: query.sortBy || undefined,
        sortDirection: query.sortDirection || undefined,
        includeDeleted: query.includeDeleted || undefined,
        onlySuperAdmins: query.onlySuperAdmins || undefined,
        userType:
          query.userType && query.userType !== "ALL"
            ? query.userType
            : undefined,
      },
    });
    return response.data;
  } catch (err) {
    throw new Error(
      err?.response?.data?.error || "Failed to load super admin users."
    );
  }
};

export const createSuperAdminUser = async (token, payload) => {
  try {
    const response = await axios.post(
      SUPER_ADMIN_USERS_URL,
      {
        email: payload.email,
        userName: payload.userName,
        firstName: payload.firstName,
        lastName: payload.lastName,
        userType: payload.userType,
        roles: payload.roles,
        sendInvite: payload.sendInvite,
        initialPassword: payload.initialPassword || null,
        notes: payload.notes || null,
      },
      withAuth(token)
    );
    return response.data;
  } catch (err) {
    throw new Error(
      err?.response?.data?.error || "Failed to create new user."
    );
  }
};

export const impersonateUser = async (token, userId) => {
  try {
    const response = await axios.post(
      SUPER_ADMIN_IMPERSONATE_URL(userId),
      {},
      withAuth(token)
    );
    return response.data;
  } catch (err) {
    throw new Error(
      err?.response?.data?.error || "Unable to impersonate the selected user."
    );
  }
};

export const stopImpersonation = async (token) => {
  try {
    const response = await axios.post(
      STOP_IMPERSONATION_URL,
      {},
      withAuth(token)
    );
    return response.data;
  } catch (err) {
    throw new Error(
      err?.response?.data?.error ||
        "Unable to restore the original administrator session."
    );
  }
};

export const suspendUser = async (token, userId, payload) => {
  try {
    const response = await axios.post(
      SUPER_ADMIN_SUSPEND_URL(userId),
      {
        untilUtc: payload.untilUtc || null,
        reason: payload.reason || null,
      },
      withAuth(token)
    );
    return response.data;
  } catch (err) {
    throw new Error(
      err?.response?.data?.error || "Failed to suspend the user."
    );
  }
};

export const unsuspendUser = async (token, userId) => {
  try {
    const response = await axios.post(
      SUPER_ADMIN_UNSUSPEND_URL(userId),
      {},
      withAuth(token)
    );
    return response.data;
  } catch (err) {
    throw new Error(
      err?.response?.data?.error || "Failed to remove the suspension."
    );
  }
};

export const softDeleteUser = async (token, userId, reason) => {
  try {
    await axios.delete(SUPER_ADMIN_USER_URL(userId), {
      ...withAuth(token),
      data: reason ? { reason } : undefined,
    });
  } catch (err) {
    throw new Error(
      err?.response?.data?.error || "Failed to delete the user."
    );
  }
};

export const hardDeleteUser = async (token, userId, reason) => {
  try {
    await axios.delete(SUPER_ADMIN_HARD_DELETE_URL(userId), {
      ...withAuth(token),
      data: reason ? { reason } : undefined,
    });
  } catch (err) {
    throw new Error(
      err?.response?.data?.error || "Failed to hard delete the user."
    );
  }
};

export const updateUserRoles = async (token, userId, payload) => {
  try {
    const normalizedRoles = Array.isArray(payload?.roles)
      ? payload.roles.filter(Boolean)
      : [];
    const nextUserType =
      payload?.userType && typeof payload.userType === "string"
        ? payload.userType.trim().toUpperCase()
        : undefined;

    const response = await axios.put(
      SUPER_ADMIN_UPDATE_ROLES_URL(userId),
      {
        roles: normalizedRoles,
        userType: nextUserType,
      },
      withAuth(token)
    );
    return response.data;
  } catch (err) {
    throw new Error(
      err?.response?.data?.error || "Failed to update user roles."
    );
  }
};

export const fetchTestDataStatus = async (token) => {
  try {
    const response = await axios.get(
      SUPER_ADMIN_TEST_DATA_URL,
      withAuth(token)
    );
    return response.data;
  } catch (err) {
    throw new Error(
      err?.response?.data?.error || "Failed to load test data status."
    );
  }
};

export const generateTestData = async (token, payload) => {
  try {
    const response = await axios.post(
      SUPER_ADMIN_TEST_DATA_URL,
      {
        numberOfUsers: payload.numberOfUsers,
        minPostsPerUser: payload.minPostsPerUser,
        maxPostsPerUser: payload.maxPostsPerUser,
        mediaPreference: payload.mediaPreference,
      },
      withAuth(token)
    );
    return response.data;
  } catch (err) {
    throw new Error(
      err?.response?.data?.error || "Failed to generate test data."
    );
  }
};

export const disableTestData = async (token, options = {}) => {
  try {
    const response = await axios.delete(SUPER_ADMIN_TEST_DATA_URL, {
      ...withAuth(token),
      params: {
        force: options.force ? true : undefined,
      },
    });
    return response.data;
  } catch (err) {
    const enhancedError = new Error(
      err?.response?.data?.error || "Failed to disable test data."
    );
    if (err?.response?.data?.conflicts) {
      enhancedError.conflicts = err.response.data.conflicts;
    }
    enhancedError.status = err?.response?.status;
    throw enhancedError;
  }
};

export const fetchSuperAdminLogs = async (token, query) => {
  try {
    const response = await axios.get(SUPER_ADMIN_LOGS_URL, {
      ...withAuth(token),
      params: {
        page: query.page,
        pageSize: query.pageSize,
        search: query.search || undefined,
        verb: query.verb || undefined,
        userId: query.userId || undefined,
        fromUtc: query.fromUtc || undefined,
        toUtc: query.toUtc || undefined,
      },
    });
    return response.data;
  } catch (err) {
    throw new Error(
      err?.response?.data?.error || "Failed to load audit logs."
    );
  }
};
